import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ItemFormDialog } from "@/components/ItemFormDialog";
import { MarkRenewedDialog } from "@/components/MarkRenewedDialog";
import { STATUS_CONFIG, fmtDate, fmtDateTime, daysLabel } from "@/lib/status";
import { ArrowLeft, BellRing, CheckCircle2, Lock, Pencil, RotateCw, Trash2 } from "lucide-react";

const DetailRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-6 py-2.5">
    <span className="text-sm text-muted-foreground shrink-0">{label}</span>
    <span className="text-sm font-medium text-right">{children}</span>
  </div>
);

export default function ItemDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);

  const canDelete = ["owner", "admin"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [itemRes, catsRes] = await Promise.all([
        api.get(`/items/${itemId}`),
        api.get("/categories"),
      ]);
      setItem(itemRes.data);
      setCategories(catsRes.data.categories);
    } catch (e) {
      setNotFound(true);
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try {
      await api.delete(`/items/${itemId}`);
      toast.success("Item deleted.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (notFound) {
    return (
      <div className="max-w-md mx-auto text-center py-16 animate-fade-up" data-testid="item-not-found">
        <h1 className="text-xl font-semibold">Item not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">It may have been deleted, or the link is wrong.</p>
        <Link to="/dashboard"><Button variant="secondary" className="mt-6 rounded-xl">Back to dashboard</Button></Link>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid md:grid-cols-3 gap-6">
          <Skeleton className="h-72 md:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  const thresholdNote = {
    item: "set on this item",
    category: `from the ${item.category_name} category`,
    organization: "organization default",
  }[item.threshold_source];

  return (
    <div className="animate-fade-up">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="item-back-link">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </Link>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight break-words" data-testid="item-detail-name">{item.name}</h1>
            <StatusBadge status={item.status} />
          </div>
          <p className={`mt-1.5 text-sm font-medium tabular-nums ${STATUS_CONFIG[item.status]?.text || ""}`}>
            {item.renewed ? `Archived ${fmtDate(item.renewed_at?.slice(0, 10))}` : `${daysLabel(item)} — due ${fmtDate(item.due_date)}`}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {!item.renewed && (
            <Button onClick={() => setRenewOpen(true)} className="rounded-xl" data-testid="item-detail-mark-renewed-button">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark renewed
            </Button>
          )}
          <Button variant="secondary" onClick={() => setEditOpen(true)} className="rounded-xl" data-testid="item-detail-edit-button">
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-[hsl(var(--destructive))]" aria-label="Delete item" data-testid="item-detail-delete-button">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this item?</AlertDialogTitle>
                  <AlertDialogDescription>
                    “{item.name}” and its reminder history will be permanently removed. This can't be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-xl">Keep it</AlertDialogCancel>
                  <AlertDialogAction onClick={doDelete} className="rounded-xl bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90" data-testid="item-detail-delete-confirm-button">
                    Delete item
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border">
            <DetailRow label="Category">{item.category_name}</DetailRow>
            <DetailRow label="Issued by">{item.issuer || "—"}</DetailRow>
            <DetailRow label="Due date"><span className="tabular-nums">{fmtDate(item.due_date)}</span></DetailRow>
            <DetailRow label="Reminder window">
              {item.effective_threshold} days before <span className="text-muted-foreground font-normal">({thresholdNote})</span>
            </DetailRow>
            <DetailRow label={<span className="inline-flex items-center gap-1">Reference <Lock className="h-3 w-3" /></span>}>
              {item.reference ? <span className="font-mono-ref" data-testid="item-detail-reference">{item.reference}</span> : "—"}
            </DetailRow>
            {item.notes && (
              <div className="py-3">
                <p className="text-sm text-muted-foreground mb-1.5">Notes</p>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{item.notes}</p>
              </div>
            )}
            <DetailRow label="Added by">{item.created_by_name || "—"}</DetailRow>
            <DetailRow label="Added on"><span className="tabular-nums">{fmtDate(item.created_at?.slice(0, 10))}</span></DetailRow>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BellRing className="h-4 w-4 text-muted-foreground" /> Reminders sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!item.reminder_logs || item.reminder_logs.length === 0) ? (
                <p className="text-sm text-muted-foreground leading-relaxed" data-testid="item-no-reminders">
                  Nothing yet. This item will appear in your team's daily summary email once it enters its reminder window.
                </p>
              ) : (
                <ul className="space-y-3" data-testid="item-reminder-logs">
                  {item.reminder_logs.map((log) => (
                    <li key={log.id} className="text-sm">
                      <p className="font-medium">{log.kind === "overdue" ? "Daily summary — overdue" : "Daily summary — due soon"}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {fmtDateTime(log.sent_at)} · email to {log.recipients?.length || 0} recipient{(log.recipients?.length || 0) !== 1 ? "s" : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {item.renewal_history?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <RotateCw className="h-4 w-4 text-muted-foreground" /> Renewal history
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {[...item.renewal_history].reverse().map((h, idx) => (
                    <li key={idx} className="text-sm">
                      <p className="font-medium">
                        {h.new_due_date ? <>Renewed — next due <span className="tabular-nums">{fmtDate(h.new_due_date)}</span></> : "Renewed and archived"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(h.renewed_at)} · by {h.renewed_by}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ItemFormDialog open={editOpen} onOpenChange={setEditOpen} categories={categories} item={item} onSaved={() => load()} />
      <MarkRenewedDialog open={renewOpen} onOpenChange={setRenewOpen} item={item} onSaved={() => load()} />
    </div>
  );
}
